import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
    uniqueKey = "uniqueKey"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
        [fields.uniqueKey]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        fieldValue : state.components.contactUsForm.fieldValues[ownProps.fields[fields.uniqueKey]],
    }
};

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ContactFormTextFieldStyles {
    contactFormTextField?: TextFieldPresentationProps;
}

export const contactFormTextFieldStyles: ContactFormTextFieldStyles = {};

const styles = contactFormTextFieldStyles;

const ContactFormTextField: React.FC<Props> = ({ fields, fieldValue, setFieldValue }) => {
    const { uniqueKey, label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [contactFormTextFieldErrMsg, setContactFormTextFieldErrMsg] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators[uniqueKey] = validateContactFormTextField;
        if (typeof fieldValue !== "undefined") {
            validateContactFormTextField();
        }
    }, [fieldValue]);

    React.useEffect(() => {
        contactFormContext.validators[uniqueKey] = validateContactFormTextField;
    }, [isRequired]);

    const validateContactFormTextField = () => {
        const errorMessage = isRequired && !fieldValue ? (siteMessage("Field_Required", label) as string) : "";
        setContactFormTextFieldErrMsg(errorMessage);
        return !errorMessage;
    };

    const contactFormTextFieldHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: uniqueKey, value: event.target.value });
    };

    return (
        <TextField
            {...styles.contactFormTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={fieldValue || ""}
            error={contactFormTextFieldErrMsg}
            onChange={contactFormTextFieldHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ContactFormTextField),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.uniqueKey,
                displayName: "Field key",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
